import { useCallback } from 'react';

import type { AIAnswerContextType } from '../../../../ai-answer-dialog/ai-answer-context';
import { isSourceProduct3P } from '../../../../ai-answer-dialog/analytics-helpers';
import { onAIAnswerSourceClick } from '../../../../ai-answer-dialog/on-ai-answer-source-click';
import type {
	NLPSearchResultSource,
	SourceSmartCardType,
} from '../../../../ai-answer-dialog/types';
import { SainAction } from '../../../controller/analytics/hooks/sain-action';
import { useSAINUsageCriteriaMet } from '../../../controller/analytics/hooks/use-sain-usage';
import { useAnalytics } from '../../../controller/analytics/use-analytics';
import { SAINFeatureControl } from '../../../utils/sain-feature-control';

/**
 * Shared hook for KnowledgeSmartCard analytics in AI answer contexts.
 * Returns a callback that fires analytics and SAIN usage events when a smart card is clicked.
 *
 * @param params - Hook parameters
 * @param params.aiAnswerDialogContext - The AI answer context from useAIAnswerContext()
 * @param params.isCitation - Whether the smart card is a citation/source (default: false)
 * @returns Object with handleSmartCardClick callback and searchSessionId
 * @throws {Error} If context is invalid or missing required properties
 *
 * @example
 * const context = useAIAnswerContext();
 * const { handleSmartCardClick, searchSessionId } = useKnowledgeSmartCardAnalytics({
 *   aiAnswerDialogContext: context
 * });
 *
 * @remarks
 * This hook requires the component to be wrapped in both:
 * - AIAnswerContext (via AIAnswerContextProvider)
 * - AIEventsInstrumentationProvider (for analytics)
 */
export const useKnowledgeSmartCardAnalytics: ({
	aiAnswerDialogContext,
	isCitation,
}: {
	aiAnswerDialogContext: AIAnswerContextType;
	isCitation?: boolean;
}) => {
	handleSmartCardClick: ({
		index,
		source,
		hostname,
		sourceCardType,
		isAuxClick,
		isContextMenu,
	}: {
		sourceCardType: SourceSmartCardType;
		index: number;
		source: NLPSearchResultSource | null;
		hostname?: string;
		isAuxClick?: boolean;
		isContextMenu?: boolean;
	}) => void;
	searchSessionId: string;
} = ({
	aiAnswerDialogContext,
	isCitation = false,
}: {
	aiAnswerDialogContext: AIAnswerContextType;
	isCitation?: boolean;
}) => {
	const { fireSAINUsageEvent } = useSAINUsageCriteriaMet();
	const [fireAnalyticsEvent] = useAnalytics();

	// Validate context
	if (!aiAnswerDialogContext || !aiAnswerDialogContext.commonAttributes) {
		throw new Error(
			'useKnowledgeSmartCardAnalytics requires a valid AIAnswerContext. ' +
				'Ensure you pass the context from useAIAnswerContext().',
		);
	}

	const { commonAttributes, callbacks } = aiAnswerDialogContext;

	const handleSmartCardClick = useCallback(
		({
			index,
			source,
			hostname,
			sourceCardType,
			isAuxClick,
			isContextMenu,
		}: {
			sourceCardType: SourceSmartCardType;
			index: number;
			source: NLPSearchResultSource | null;
			hostname?: string;
			isAuxClick?: boolean;
			isContextMenu?: boolean;
		}): void => {
			if ((isAuxClick || isContextMenu) && !SAINFeatureControl.isNonPrimaryClickTrackingEnabled()) {
				return;
			}
			fireAnalyticsEvent(
				onAIAnswerSourceClick({
					sourceIndex: index,
					hostname,
					isAuxClick,
					isContextMenu,
					sourceCardType,
					sourceProduct: source?.product || '',
					is3PSource: isSourceProduct3P(source?.product),
					isCitation: !!isCitation,
					...commonAttributes,
				}),
			);
			fireSAINUsageEvent({
				metCriteria: SainAction.SOURCE_CLICKED,
				commonAttributes,
				sourceProduct: source?.product || '',
			});
			callbacks?.onAIAnswerSourceClick?.();
		},
		[fireAnalyticsEvent, commonAttributes, fireSAINUsageEvent, callbacks, isCitation],
	);

	return {
		handleSmartCardClick,
		searchSessionId: commonAttributes.searchSessionId,
	};
};
